import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import type { RegisteredBroker, RegisteredLender, UnregisteredBrokerLead, UnregisteredBrokerLeadWithSource, UnregisteredLenderLead, UnregisteredLenderLeadWithSource } from "@shared/schema";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Building2, Edit2, Plus, Trash2, UserCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { LeadNotesPopover } from "./LeadNotesPopover";
import { RegisteredNotesPopover } from "./RegisteredNotesPopover";

export function LendersBrokersPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [lenderDialog, setLenderDialog] = useState<{ open: boolean; editing: RegisteredLender | null; fromLeadId: number | null }>({ open: false, editing: null, fromLeadId: null });
  const [brokerDialog, setBrokerDialog] = useState<{ open: boolean; editing: RegisteredBroker | null; fromLeadId: number | null }>({ open: false, editing: null, fromLeadId: null });
  const [lenderForm, setLenderForm] = useState({ contactName: "", email: "", phone: "", companyName: "", notes: "", showOnFinancePage: false, blurb: "", website: "", logoUrl: "", statesLicensed: "", loanTypes: "" });
  const [brokerForm, setBrokerForm] = useState({ contactName: "", email: "", phone: "", companyName: "", notes: "", showOnFinancePage: false, blurb: "", website: "", logoUrl: "", statesLicensed: "" });
  // When publishing is turned OFF on a previously-published record, ask whether
  // to keep or deactivate the auto-created partner account before saving.
  const [unpublishChoice, setUnpublishChoice] = useState<{ kind: "lender" | "broker" } | null>(null);

  const lendersQuery = useQuery<RegisteredLender[]>({ queryKey: ["/api/admin/referrals/lenders"] });
  const brokersQuery = useQuery<RegisteredBroker[]>({ queryKey: ["/api/admin/referrals/brokers"] });
  const lenderLeadsQuery = useQuery<UnregisteredLenderLeadWithSource[]>({ queryKey: ["/api/admin/referrals/leads/lenders"] });
  const brokerLeadsQuery = useQuery<UnregisteredBrokerLeadWithSource[]>({ queryKey: ["/api/admin/referrals/leads/brokers"] });

  const updateLenderLeadMutation = useMutation({
    mutationFn: async (vars: { id: number; status: string }) =>
      apiRequest("PATCH", `/api/admin/referrals/leads/lenders/${vars.id}`, { status: vars.status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/leads/lenders"] }),
  });

  const updateBrokerLeadMutation = useMutation({
    mutationFn: async (vars: { id: number; status: string }) =>
      apiRequest("PATCH", `/api/admin/referrals/leads/brokers/${vars.id}`, { status: vars.status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/leads/brokers"] }),
  });

  const toggleLenderActiveMutation = useMutation({
    mutationFn: async (vars: { id: number; active: boolean }) =>
      apiRequest("PATCH", `/api/admin/referrals/lenders/${vars.id}`, { active: vars.active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/lenders"] }),
    onError: (err: unknown) =>
      toast({
        title: "Failed to update visibility",
        description: parseErrorMessage(err, "Couldn't update lender visibility. Please try again."),
        variant: "destructive",
      }),
  });

  const saveLenderMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (lenderDialog.editing) {
        return apiRequest("PATCH", `/api/admin/referrals/lenders/${lenderDialog.editing.id}`, data);
      }
      return apiRequest("POST", "/api/admin/referrals/lenders", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/lenders"] });
      const fromLeadId = lenderDialog.fromLeadId;
      const wasEditing = !!lenderDialog.editing;
      setLenderDialog({ open: false, editing: null, fromLeadId: null });
      toast({ title: wasEditing ? "Lender updated" : "Lender added" });
      if (fromLeadId != null) {
        updateLenderLeadMutation.mutate({ id: fromLeadId, status: "added_to_platform" });
      }
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to save lender",
        description: parseErrorMessage(err, "Couldn't save this lender. Please try again."),
        variant: "destructive",
      }),
  });

  const deleteLenderMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/admin/referrals/lenders/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/lenders"] });
      toast({ title: "Lender deleted" });
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to delete lender",
        description: parseErrorMessage(err, "Couldn't delete this lender. Please try again."),
        variant: "destructive",
      }),
  });

  const toggleBrokerActiveMutation = useMutation({
    mutationFn: async (vars: { id: number; active: boolean }) =>
      apiRequest("PATCH", `/api/admin/referrals/brokers/${vars.id}`, { active: vars.active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/brokers"] }),
  });

  const saveBrokerMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (brokerDialog.editing) {
        return apiRequest("PATCH", `/api/admin/referrals/brokers/${brokerDialog.editing.id}`, data);
      }
      return apiRequest("POST", "/api/admin/referrals/brokers", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/brokers"] });
      const fromLeadId = brokerDialog.fromLeadId;
      const wasEditing = !!brokerDialog.editing;
      setBrokerDialog({ open: false, editing: null, fromLeadId: null });
      toast({ title: wasEditing ? "Broker updated" : "Broker added" });
      if (fromLeadId != null) {
        updateBrokerLeadMutation.mutate({ id: fromLeadId, status: "added_to_platform" });
      }
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to save broker",
        description: parseErrorMessage(err, "Couldn't save this broker. Please try again."),
        variant: "destructive",
      }),
  });

  const deleteBrokerMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/admin/referrals/brokers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/brokers"] });
      toast({ title: "Broker deleted" });
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to delete broker",
        description: parseErrorMessage(err, "Couldn't delete this broker. Please try again."),
        variant: "destructive",
      }),
  });

  function openEditLender(l: RegisteredLender) {
    setLenderForm({
      contactName: l.contactName, email: l.email, phone: l.phone || "", companyName: l.companyName || "", notes: l.notes || "",
      showOnFinancePage: l.showOnFinancePage ?? false, blurb: l.blurb || "", website: l.website || "", logoUrl: l.logoUrl || "",
      statesLicensed: (l.statesLicensed ?? []).join(", "), loanTypes: (l.loanTypes ?? []).join(", "),
    });
    setLenderDialog({ open: true, editing: l, fromLeadId: null });
  }

  function openEditBroker(b: RegisteredBroker) {
    setBrokerForm({
      contactName: b.contactName, email: b.email, phone: b.phone || "", companyName: b.companyName || "", notes: b.notes || "",
      showOnFinancePage: b.showOnFinancePage ?? false, blurb: b.blurb || "", website: b.website || "", logoUrl: b.logoUrl || "",
      statesLicensed: (b.statesLicensed ?? []).join(", "),
    });
    setBrokerDialog({ open: true, editing: b, fromLeadId: null });
  }

  const csvToArr = (s: string) => s.split(",").map(x => x.trim()).filter(Boolean);

  function buildLenderPayload(partnerOnUnpublish?: "keep" | "deactivate") {
    return {
      contactName: lenderForm.contactName,
      email: lenderForm.email,
      phone: lenderForm.phone,
      companyName: lenderForm.companyName,
      notes: lenderForm.notes,
      showOnFinancePage: lenderForm.showOnFinancePage,
      blurb: lenderForm.blurb,
      website: lenderForm.website,
      logoUrl: lenderForm.logoUrl,
      statesLicensed: csvToArr(lenderForm.statesLicensed),
      loanTypes: csvToArr(lenderForm.loanTypes),
      ...(partnerOnUnpublish ? { partnerOnUnpublish } : {}),
    };
  }

  function buildBrokerPayload(partnerOnUnpublish?: "keep" | "deactivate") {
    return {
      contactName: brokerForm.contactName,
      email: brokerForm.email,
      phone: brokerForm.phone,
      companyName: brokerForm.companyName,
      notes: brokerForm.notes,
      showOnFinancePage: brokerForm.showOnFinancePage,
      blurb: brokerForm.blurb,
      website: brokerForm.website,
      logoUrl: brokerForm.logoUrl,
      statesLicensed: csvToArr(brokerForm.statesLicensed),
      ...(partnerOnUnpublish ? { partnerOnUnpublish } : {}),
    };
  }

  function handleSaveLender() {
    const turningOff = !!lenderDialog.editing && lenderDialog.editing.showOnFinancePage && !lenderForm.showOnFinancePage;
    if (turningOff) { setUnpublishChoice({ kind: "lender" }); return; }
    saveLenderMutation.mutate(buildLenderPayload());
  }

  function handleSaveBroker() {
    const turningOff = !!brokerDialog.editing && brokerDialog.editing.showOnFinancePage && !brokerForm.showOnFinancePage;
    if (turningOff) { setUnpublishChoice({ kind: "broker" }); return; }
    saveBrokerMutation.mutate(buildBrokerPayload());
  }

  function confirmUnpublish(partnerOnUnpublish: "keep" | "deactivate") {
    if (unpublishChoice?.kind === "lender") {
      saveLenderMutation.mutate(buildLenderPayload(partnerOnUnpublish));
    } else if (unpublishChoice?.kind === "broker") {
      saveBrokerMutation.mutate(buildBrokerPayload(partnerOnUnpublish));
    }
    setUnpublishChoice(null);
  }

  function openRegisterLenderFromLead(lead: UnregisteredLenderLead) {
    const noteParts: string[] = [];
    if (lead.capturedAtStage) noteParts.push(`Captured during ${lead.capturedAtStage}`);
    if (lead.adminNotes) noteParts.push(lead.adminNotes);
    setLenderForm({
      contactName: lead.contactName,
      email: lead.email,
      phone: lead.phone || "",
      companyName: lead.companyName || "",
      notes: noteParts.join(" — "),
      showOnFinancePage: false, blurb: "", website: "", logoUrl: "", statesLicensed: "", loanTypes: "",
    });
    setLenderDialog({ open: true, editing: null, fromLeadId: lead.id });
  }

  function openRegisterBrokerFromLead(lead: UnregisteredBrokerLead) {
    const noteParts: string[] = [];
    if (lead.capturedAtStage) noteParts.push(`Captured during ${lead.capturedAtStage}`);
    if (lead.adminNotes) noteParts.push(lead.adminNotes);
    setBrokerForm({
      contactName: lead.contactName,
      email: lead.email,
      phone: lead.phone || "",
      companyName: lead.companyName || "",
      notes: noteParts.join(" — "),
      showOnFinancePage: false, blurb: "", website: "", logoUrl: "", statesLicensed: "",
    });
    setBrokerDialog({ open: true, editing: null, fromLeadId: lead.id });
  }

  const activeLenderLeads = (lenderLeadsQuery.data ?? []).filter(l => l.status !== "added_to_platform");
  const activeBrokerLeads = (brokerLeadsQuery.data ?? []).filter(l => l.status !== "added_to_platform");

  return (
    <div className="space-y-6">
      {/* Lenders */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-brand" />
            <h3 className="font-semibold text-sm">Registered Lenders</h3>
            <Badge variant="secondary" className="text-xs">{lendersQuery.data?.length ?? 0}</Badge>
          </div>
          <Button size="sm" onClick={() => { setLenderForm({ contactName: "", email: "", phone: "", companyName: "", notes: "", showOnFinancePage: false, blurb: "", website: "", logoUrl: "", statesLicensed: "", loanTypes: "" }); setLenderDialog({ open: true, editing: null, fromLeadId: null }); }} data-testid="button-add-lender">
            <Plus className="h-4 w-4 mr-1" /> Add Lender
          </Button>
        </div>
        <div className="overflow-x-auto">
          {lendersQuery.isLoading ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (lendersQuery.data?.length ?? 0) === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No registered lenders yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Company</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Phone</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Notes</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Visible to users</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {lendersQuery.data?.map(l => (
                  <tr key={l.id} data-testid={`row-lender-${l.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium" data-testid={`text-lender-name-${l.id}`}>{l.contactName}</td>
                    <td className="px-4 py-2 text-gray-600" data-testid={`text-lender-company-${l.id}`}>{l.companyName || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{l.email}</td>
                    <td className="px-4 py-2 text-gray-600">{l.phone || "—"}</td>
                    <td className="px-4 py-2"><RegisteredNotesPopover entityType="lenders" entityId={l.id} currentNotes={l.notes} /></td>
                    <td className="px-4 py-2">
                      <Switch
                        checked={l.active}
                        onCheckedChange={(checked) => toggleLenderActiveMutation.mutate({ id: l.id, active: checked })}
                        disabled={toggleLenderActiveMutation.isPending}
                        data-testid={`toggle-lender-active-${l.id}`}
                      />
                    </td>
                    <td className="px-4 py-2 flex items-center gap-2 justify-end">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditLender(l)} data-testid={`button-edit-lender-${l.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => deleteLenderMutation.mutate(l.id)} data-testid={`button-delete-lender-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Brokers */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-green-600" />
            <h3 className="font-semibold text-sm">Registered Brokers / Wholesalers</h3>
            <Badge variant="secondary" className="text-xs">{brokersQuery.data?.length ?? 0}</Badge>
          </div>
          <Button size="sm" onClick={() => { setBrokerForm({ contactName: "", email: "", phone: "", companyName: "", notes: "", showOnFinancePage: false, blurb: "", website: "", logoUrl: "", statesLicensed: "" }); setBrokerDialog({ open: true, editing: null, fromLeadId: null }); }} data-testid="button-add-broker">
            <Plus className="h-4 w-4 mr-1" /> Add Broker
          </Button>
        </div>
        <div className="overflow-x-auto">
          {brokersQuery.isLoading ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (brokersQuery.data?.length ?? 0) === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No registered brokers yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Company</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Phone</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Notes</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Visible to users</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {brokersQuery.data?.map(b => (
                  <tr key={b.id} data-testid={`row-broker-${b.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium" data-testid={`text-broker-name-${b.id}`}>{b.contactName}</td>
                    <td className="px-4 py-2 text-gray-600" data-testid={`text-broker-company-${b.id}`}>{b.companyName || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{b.email}</td>
                    <td className="px-4 py-2 text-gray-600">{b.phone || "—"}</td>
                    <td className="px-4 py-2"><RegisteredNotesPopover entityType="brokers" entityId={b.id} currentNotes={b.notes} /></td>
                    <td className="px-4 py-2">
                      <Switch
                        checked={b.active}
                        onCheckedChange={(checked) => toggleBrokerActiveMutation.mutate({ id: b.id, active: checked })}
                        disabled={toggleBrokerActiveMutation.isPending}
                        data-testid={`toggle-broker-active-${b.id}`}
                      />
                    </td>
                    <td className="px-4 py-2 flex items-center gap-2 justify-end">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditBroker(b)} data-testid={`button-edit-broker-${b.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => deleteBrokerMutation.mutate(b.id)} data-testid={`button-delete-broker-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Unregistered Lender Leads */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-amber-600" />
            <h3 className="font-semibold text-sm">Unregistered Lender Leads</h3>
            <Badge variant="secondary" className="text-xs" data-testid="badge-lender-leads-count">{activeLenderLeads.length}</Badge>
          </div>
          <p className="text-xs text-gray-500">Captured from closing-team submissions. Register them so future deals trigger invoices.</p>
        </div>
        <div className="overflow-x-auto">
          {lenderLeadsQuery.isLoading ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : activeLenderLeads.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="text-lender-leads-empty">No unregistered lender leads.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Contact</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Company</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Phone</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">From</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Stage</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Captured</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Notes</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {activeLenderLeads.map(lead => (
                  <tr key={lead.id} data-testid={`row-lender-lead-${lead.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium" data-testid={`text-lender-lead-name-${lead.id}`}>{lead.contactName}</td>
                    <td className="px-4 py-2 text-gray-600">{lead.companyName || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{lead.email}</td>
                    <td className="px-4 py-2 text-gray-600">{lead.phone || "—"}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      <div className="font-medium text-gray-700" data-testid={`text-lender-lead-property-${lead.id}`}>{lead.propertyAddress || "—"}</div>
                      {(lead.capturedByName || lead.capturedByEmail) && (
                        <div className="text-gray-500" data-testid={`text-lender-lead-captured-by-${lead.id}`}>by {lead.capturedByName || lead.capturedByEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{lead.capturedAtStage || "—"}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{new Date(lead.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className="text-xs capitalize">{lead.status.replace(/_/g, " ")}</Badge></td>
                    <td className="px-4 py-2">
                      <LeadNotesPopover leadType="lenders" leadId={lead.id} currentNotes={lead.adminNotes} />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" className="h-7 text-xs" onClick={() => openRegisterLenderFromLead(lead)} data-testid={`button-register-lender-lead-${lead.id}`}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Register
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500" onClick={() => updateLenderLeadMutation.mutate({ id: lead.id, status: "not_interested" })} disabled={updateLenderLeadMutation.isPending} data-testid={`button-dismiss-lender-lead-${lead.id}`}>
                          Dismiss
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Unregistered Broker Leads */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-amber-600" />
            <h3 className="font-semibold text-sm">Unregistered Broker Leads</h3>
            <Badge variant="secondary" className="text-xs" data-testid="badge-broker-leads-count">{activeBrokerLeads.length}</Badge>
          </div>
          <p className="text-xs text-gray-500">Captured from closing-team submissions. Register them so future deals trigger invoices.</p>
        </div>
        <div className="overflow-x-auto">
          {brokerLeadsQuery.isLoading ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : activeBrokerLeads.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="text-broker-leads-empty">No unregistered broker leads.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Contact</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Company</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Phone</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">From</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Stage</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Captured</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Notes</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {activeBrokerLeads.map(lead => (
                  <tr key={lead.id} data-testid={`row-broker-lead-${lead.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium" data-testid={`text-broker-lead-name-${lead.id}`}>{lead.contactName}</td>
                    <td className="px-4 py-2 text-gray-600">{lead.companyName || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{lead.email}</td>
                    <td className="px-4 py-2 text-gray-600">{lead.phone || "—"}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      <div className="font-medium text-gray-700" data-testid={`text-broker-lead-property-${lead.id}`}>{lead.propertyAddress || "—"}</div>
                      {(lead.capturedByName || lead.capturedByEmail) && (
                        <div className="text-gray-500" data-testid={`text-broker-lead-captured-by-${lead.id}`}>by {lead.capturedByName || lead.capturedByEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{lead.capturedAtStage || "—"}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{new Date(lead.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className="text-xs capitalize">{lead.status.replace(/_/g, " ")}</Badge></td>
                    <td className="px-4 py-2">
                      <LeadNotesPopover leadType="brokers" leadId={lead.id} currentNotes={lead.adminNotes} />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" className="h-7 text-xs" onClick={() => openRegisterBrokerFromLead(lead)} data-testid={`button-register-broker-lead-${lead.id}`}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Register
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500" onClick={() => updateBrokerLeadMutation.mutate({ id: lead.id, status: "not_interested" })} disabled={updateBrokerLeadMutation.isPending} data-testid={`button-dismiss-broker-lead-${lead.id}`}>
                          Dismiss
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Lender Add/Edit Dialog */}
      <Dialog open={lenderDialog.open} onOpenChange={(o) => { if (!o) setLenderDialog({ open: false, editing: null, fromLeadId: null }); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="dialog-lender-form">
          <DialogHeader>
            <DialogTitle>{lenderDialog.editing ? "Edit Lender" : "Add Lender"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-500">Name *</Label><Input className="mt-0.5" value={lenderForm.contactName} onChange={e => setLenderForm(p => ({ ...p, contactName: e.target.value }))} data-testid="input-lender-name" /></div>
            <div><Label className="text-xs text-gray-500">Email *</Label><Input className="mt-0.5" value={lenderForm.email} onChange={e => setLenderForm(p => ({ ...p, email: e.target.value }))} data-testid="input-lender-email" /></div>
            <div><Label className="text-xs text-gray-500">Phone</Label><Input className="mt-0.5" value={lenderForm.phone} onChange={e => setLenderForm(p => ({ ...p, phone: e.target.value }))} data-testid="input-lender-phone" /></div>
            <div><Label className="text-xs text-gray-500">Company</Label><Input className="mt-0.5" value={lenderForm.companyName} onChange={e => setLenderForm(p => ({ ...p, companyName: e.target.value }))} data-testid="input-lender-company" /></div>
            <div><Label className="text-xs text-gray-500">States Licensed (comma-separated)</Label><Input className="mt-0.5" value={lenderForm.statesLicensed} onChange={e => setLenderForm(p => ({ ...p, statesLicensed: e.target.value }))} placeholder="GA, TX, FL" data-testid="input-lender-states" /></div>
            <div><Label className="text-xs text-gray-500">Loan Types (comma-separated)</Label><Input className="mt-0.5" value={lenderForm.loanTypes} onChange={e => setLenderForm(p => ({ ...p, loanTypes: e.target.value }))} placeholder="Conventional, DSCR, FHA" data-testid="input-lender-loan-types" /></div>
            <div><Label className="text-xs text-gray-500">Notes</Label><textarea className="mt-0.5 w-full border rounded-md p-2 text-sm resize-none h-20" value={lenderForm.notes} onChange={e => setLenderForm(p => ({ ...p, notes: e.target.value }))} data-testid="textarea-lender-notes" /></div>
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-gray-900">Show on Finance page</Label>
                  <p className="text-xs text-gray-500">Publishes a card on /app/finance and creates a partner account.</p>
                </div>
                <Switch checked={lenderForm.showOnFinancePage} onCheckedChange={c => setLenderForm(p => ({ ...p, showOnFinancePage: c }))} data-testid="switch-lender-finance" />
              </div>
              {lenderForm.showOnFinancePage && (
                <>
                  <div><Label className="text-xs text-gray-500">Public blurb</Label><textarea className="mt-0.5 w-full border rounded-md p-2 text-sm resize-none h-16" value={lenderForm.blurb} onChange={e => setLenderForm(p => ({ ...p, blurb: e.target.value }))} placeholder="Short description shown on the finance card" data-testid="textarea-lender-blurb" /></div>
                  <div><Label className="text-xs text-gray-500">Website</Label><Input className="mt-0.5" value={lenderForm.website} onChange={e => setLenderForm(p => ({ ...p, website: e.target.value }))} placeholder="https://" data-testid="input-lender-website" /></div>
                  <div><Label className="text-xs text-gray-500">Logo URL</Label><Input className="mt-0.5" value={lenderForm.logoUrl} onChange={e => setLenderForm(p => ({ ...p, logoUrl: e.target.value }))} placeholder="https://" data-testid="input-lender-logo" /></div>
                </>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setLenderDialog({ open: false, editing: null, fromLeadId: null })}>Cancel</Button>
            <Button onClick={() => handleSaveLender()} disabled={saveLenderMutation.isPending || !lenderForm.contactName || !lenderForm.email} data-testid="button-save-lender">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broker Add/Edit Dialog */}
      <Dialog open={brokerDialog.open} onOpenChange={(o) => { if (!o) setBrokerDialog({ open: false, editing: null, fromLeadId: null }); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="dialog-broker-form">
          <DialogHeader>
            <DialogTitle>{brokerDialog.editing ? "Edit Broker" : "Add Broker"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-500">Name *</Label><Input className="mt-0.5" value={brokerForm.contactName} onChange={e => setBrokerForm(p => ({ ...p, contactName: e.target.value }))} data-testid="input-broker-name" /></div>
            <div><Label className="text-xs text-gray-500">Email *</Label><Input className="mt-0.5" value={brokerForm.email} onChange={e => setBrokerForm(p => ({ ...p, email: e.target.value }))} data-testid="input-broker-email" /></div>
            <div><Label className="text-xs text-gray-500">Phone</Label><Input className="mt-0.5" value={brokerForm.phone} onChange={e => setBrokerForm(p => ({ ...p, phone: e.target.value }))} data-testid="input-broker-phone" /></div>
            <div><Label className="text-xs text-gray-500">Company</Label><Input className="mt-0.5" value={brokerForm.companyName} onChange={e => setBrokerForm(p => ({ ...p, companyName: e.target.value }))} data-testid="input-broker-company" /></div>
            <div><Label className="text-xs text-gray-500">States Licensed (comma-separated)</Label><Input className="mt-0.5" value={brokerForm.statesLicensed} onChange={e => setBrokerForm(p => ({ ...p, statesLicensed: e.target.value }))} placeholder="GA, TX, FL" data-testid="input-broker-states" /></div>
            <div><Label className="text-xs text-gray-500">Notes</Label><textarea className="mt-0.5 w-full border rounded-md p-2 text-sm resize-none h-20" value={brokerForm.notes} onChange={e => setBrokerForm(p => ({ ...p, notes: e.target.value }))} data-testid="textarea-broker-notes" /></div>
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-gray-900">Show on Finance page</Label>
                  <p className="text-xs text-gray-500">Publishes a card on /app/finance and creates a partner account.</p>
                </div>
                <Switch checked={brokerForm.showOnFinancePage} onCheckedChange={c => setBrokerForm(p => ({ ...p, showOnFinancePage: c }))} data-testid="switch-broker-finance" />
              </div>
              {brokerForm.showOnFinancePage && (
                <>
                  <div><Label className="text-xs text-gray-500">Public blurb</Label><textarea className="mt-0.5 w-full border rounded-md p-2 text-sm resize-none h-16" value={brokerForm.blurb} onChange={e => setBrokerForm(p => ({ ...p, blurb: e.target.value }))} placeholder="Short description shown on the finance card" data-testid="textarea-broker-blurb" /></div>
                  <div><Label className="text-xs text-gray-500">Website</Label><Input className="mt-0.5" value={brokerForm.website} onChange={e => setBrokerForm(p => ({ ...p, website: e.target.value }))} placeholder="https://" data-testid="input-broker-website" /></div>
                  <div><Label className="text-xs text-gray-500">Logo URL</Label><Input className="mt-0.5" value={brokerForm.logoUrl} onChange={e => setBrokerForm(p => ({ ...p, logoUrl: e.target.value }))} placeholder="https://" data-testid="input-broker-logo" /></div>
                </>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setBrokerDialog({ open: false, editing: null, fromLeadId: null })}>Cancel</Button>
            <Button onClick={() => handleSaveBroker()} disabled={saveBrokerMutation.isPending || !brokerForm.contactName || !brokerForm.email} data-testid="button-save-broker">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unpublish choice — keep or deactivate the partner account */}
      <Dialog open={unpublishChoice !== null} onOpenChange={(o) => { if (!o) setUnpublishChoice(null); }}>
        <DialogContent data-testid="dialog-unpublish-choice">
          <DialogHeader>
            <DialogTitle>Remove from Finance page</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will remove the card from /app/finance. What should happen to the partner-portal account that was created for this {unpublishChoice?.kind === "broker" ? "broker" : "lender"}?
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <Button variant="outline" onClick={() => confirmUnpublish("keep")} data-testid="button-unpublish-keep">Keep partner account active</Button>
            <Button variant="destructive" onClick={() => confirmUnpublish("deactivate")} data-testid="button-unpublish-deactivate">Deactivate partner account</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
